import { useState, useEffect, useContext, useRef } from 'react';
import { CurrencyContext } from '../../context/CurrencyContext';
import { getAlpha2Code } from '../../lib/helper/countryToAphlaCode';

// Unit conversion functions
const convertLbsToKg = (lbs) => {
    return lbs * 0.453592;
};

const convertInchesToCm = (inches) => {
    return inches * 2.54;
};

// Helper function to get converted weight in kg
const getWeightInKg = (weight) => {
    if (!weight || !weight.value) return 0;
    const value = parseFloat(weight.value);
    return weight.unit === 'lbs' ? convertLbsToKg(value) : value;
};

// Helper function to get converted dimension in cm
const getDimensionInCm = (dimension) => {
    if (!dimension || !dimension.value) return 0;
    const value = parseFloat(dimension.value);
    return dimension.unit === 'in' ? convertInchesToCm(value) : value;
};

const DHLService = ({ formData, trigger, onData, onError, isGuest }) => {
    const { currencies } = useContext(CurrencyContext);
    const [accessToken, setAccessToken] = useState(null);
    const [tokenExpiry, setTokenExpiry] = useState(null);
    const [pricingStrategy, setPricingStrategy] = useState("EXACT");
    const isRequesting = useRef(false); // Add flag to track request status

    const ensureValidToken = async () => {
        if (!accessToken || !tokenExpiry || Date.now() >= tokenExpiry) {
            try {
                const response = await fetch("https://api.dhl.com/auth/v1/token", {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: new URLSearchParams({
                        grant_type: "client_credentials",
                        client_id: "7E2HCWQVR0A10NJKYbYnxRt5uFT7vn1z",
                        client_secret: "uB26UUKHmBMJqbMQ",
                    }),
                });
                const data = await response.json();
                setAccessToken(data.access_token);
                setTokenExpiry(Date.now() + 15 * 60 * 1000);
                return data.access_token;
            } catch (error) {
                throw new Error("Failed to obtain access token");
            }
        }
        return accessToken;
    };

    const filterDHLResponse = (dhlResponse) => {
        if (!dhlResponse) return null;
        return {
            quoteId: dhlResponse.quoteId,
            pricingStrategy: dhlResponse.pricingStrategy,
            feeTotals: dhlResponse.feeTotals.filter(fee => fee.value > 0),
            consigneeAddress: dhlResponse.consigneeAddress,
            senderAddress: dhlResponse.senderAddress,
            packageDetails: {
                output_RELATIONSHIP_CURRENCY: dhlResponse.packageDetails.outputCurrency,
                clearanceMode: dhlResponse.packageDetails.clearanceMode,
                transportMode: dhlResponse.packageDetails.transportMode,
                endUse: dhlResponse.packageDetails.endUse,
                freightCharge: dhlResponse.packageDetails.freightCharge,
                insuranceCharge: dhlResponse.packageDetails.insuranceCharge,
            },
        };
    };

    const getDHLEstimation = async (formData, token) => {
        const userCurrency = isGuest ? "USD" : formData.userCurrency || "EUR";
        const currencyData = currencies.find(curr => curr.name === userCurrency) || { name: "EUR", exchangeRateUSD: 1.07 };
        const exchangeRateUSD = currencyData.exchangeRateUSD || 1;
        const dhlCurrency = userCurrency === "USD" ? "USD" : "EUR";

        const totalItemsValue = formData.items.reduce((sum, item) => {
            const value = parseFloat(item.itemValue) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            return sum + (value * quantity);
        }, 0);

        // Calculate total weight using conversion functions - ALWAYS convert to kg for DHL
        const totalWeightInKg = formData.dimensions.reduce((sum, dim) => {
            const weightInKg = getWeightInKg(dim.weight);
            const quantity = parseFloat(dim.quantity.value || 0);
            return sum + (weightInKg * quantity);
        }, 0);

        const requestBody = {
            pricingStrategy,
            senderAddress: { country: getAlpha2Code(formData.originCountry), state: formData.originState || formData.originCity || "" },
            consigneeAddress: { country: getAlpha2Code(formData.destinationCountry), state: formData.destinationState || formData.destinationCity || "" },
            packageDetails: {
                outputCurrency: dhlCurrency,
                transportMode: "AIR",
                endUse: "BUSINESS",
                clearanceMode: "COURIER",
                tax: { included: false, percent: 0, return: true },
                freightCharge: { value: /*parseFloat(formData.airfreight) ||*/ 0, currency: dhlCurrency },
                insuranceCharge: {
                    value: /*formData.insuranceSelected === "YES" ? parseFloat(totalItemsValue * exchangeRateUSD * 0.0075) || 0 :*/ 0,
                    currency: dhlCurrency,
                },
            },
            customsDetails: formData.items.map(item => ({
                itemId: `ITEM_${Math.random().toString(36).substr(2, 9)}`,
                itemDescription: item.itemName,
                hsCode: String(item.itemHSCode || ""),
                countryOfOrigin: getAlpha2Code(item.countryOrigin),
                itemQuantity: { value: parseInt(item.quantity), unit: "PCS" },
                itemValue: {
                    value: userCurrency === "USD"
                        ? (parseFloat(item.itemValue) * 1.1) || 0
                        : (parseFloat(item.itemValue) * exchangeRateUSD * 1.1) || 0,
                    currency: dhlCurrency,
                },
                weight: {
                    value: totalWeightInKg, // Always use converted weight in KG
                    unit: "KG" // Always use KG for DHL API
                },
            })),
        };

        try {
            const response = await fetch("https://api.dhl.com/dtc/v1/quotes", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.type?.includes("400.0904002")) {
                    setPricingStrategy("MAXIMUM");
                    requestBody.pricingStrategy = "MAXIMUM";
                    const retryResponse = await fetch("https://api.dhl.com/dtc/v1/quotes", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify(requestBody),
                    });
                    if (!retryResponse.ok) throw new Error("Failed with MAXIMUM pricing strategy");
                    const retryData = await retryResponse.json();
                    const filteredRetryData = filterDHLResponse(retryData);
                    const dutyValue = retryData.feeTotals.find(fee => fee.name === "DUTY")?.value || 0;
                    return { dhlData: filteredRetryData, dutyValue };
                }
                throw new Error(errorData.message);
            }

            const data = await response.json();
            const filteredData = filterDHLResponse(data);
            const dutyValue = data.feeTotals.find(fee => fee.name === "DUTY")?.value || 0;
            return { dhlData: filteredData, dutyValue };
        } catch (error) {
            throw error;
        }
    };

    useEffect(() => {
        if (trigger && !isRequesting.current) {
            isRequesting.current = true; // Mark request as in progress
            const fetchData = async () => {
                try {
                    const token = await ensureValidToken();
                    const result = await getDHLEstimation(formData, token);
                    onData(result);
                } catch (error) {
                    onError(error);
                } finally {
                    isRequesting.current = false; // Reset flag when done
                }
            };
            fetchData();
        }
    }, [trigger, formData, onData, onError, isGuest, currencies]);

    return null;
};

export default DHLService;